/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable new-cap */
import {Effect, Layer} from 'effect';
import {InterruptedException} from 'effect/Cause';

import {LoadProjectOptions} from 'modules/Tasks/components/Gantt/types';
import {TaskDependencyDto} from 'shared/models/TaskDependency';

import {createProjecDepsEffect} from './api/createProjecDepsEffect';
import {BatchLoader, BatchLoadConfig} from './BatchLoader';
import {EffectError} from './errors';
import {createProjectLoader} from './helpers';

const effect = Effect.gen(function* () {
  const batchLoader = yield* BatchLoader;

  const loadDependencies = ({
    projectId,
    taskIds,
  }: LoadProjectOptions): Effect.Effect<TaskDependencyDto[] | InterruptedException, EffectError, never> => {
    if (!projectId) {
      return Effect.fail(new Error('Project ID is required'));
    }

    const fetchProjectDepsBatch = createProjecDepsEffect({
      projectId,
      taskIds,
    });

    const batchEffect = batchLoader.createBatchLoadEffect({
      batchFn: fetchProjectDepsBatch,
      fnName: 'fetchProjectDepsBatch',
      projectId,
      batchSize: 6_000,
    });

    return createProjectLoader(projectId, batchEffect);
  };

  return {
    loadDependencies,
  };
});

export class ConcurrentProjectLoader extends Effect.Service<ConcurrentProjectLoader>()('ConcurrentProjectLoader', {
  effect,
  dependencies: [BatchLoader.Default],
}) {
  static Test = <T>(mockData?: T[]) => {
    // For success cases with mock data
    if (Array.isArray(mockData) || (mockData && 'tasks' in mockData)) {
      return this.DefaultWithoutDependencies.pipe(
        Layer.provide(
          Layer.succeed(BatchLoader, {
            createBatchLoadEffect: <U>(_config: BatchLoadConfig<U>): Effect.Effect<U[], Error, never> =>
              Effect.succeed(Array.isArray(mockData) ? (mockData as unknown as U[]) : ([mockData] as unknown as U[])),
            _tag: 'BatchLoader' as const,
          }),
        ),
      );
    }

    // Default case for error testing
    const defaultBatchLayer = Layer.succeed(BatchLoader, {
      createBatchLoadEffect: <T>(config: BatchLoadConfig<T>): Effect.Effect<T[], Error, never> => {
        return Effect.gen(function* () {
          const response = yield* config.batchFn(0, config.batchSize, null);
          return response.data;
        });
      },
      _tag: 'BatchLoader' as const,
    });

    return this.DefaultWithoutDependencies.pipe(Layer.provide(defaultBatchLayer));
  };
}
